import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import { useState } from "react";
import SetCauseListDialog from "@/components/system/SetCauseListDialog";
import { useDefaultCauseSets, useDeleteDefaultCauseSet } from "@/hooks/useDefaultCauses";
import type { DefaultCauseSet } from "@/interfaces/defaultCauses";
import { formatUsd } from "@/utils/number-format";

/**
 * A CALENDAR DATE (YYYY-MM-DD), rendered as the same day for every reader.
 *
 * `new Date("2026-10-01")` parses as UTC midnight, which prints as 30 September to anyone west of
 * Greenwich — the precise confusion these columns were changed to `date` to eliminate. Appending a
 * time makes it parse in local time, so the day survives the round trip.
 */
const fmtDate = (day: string | null) =>
  day
    ? new Date(`${day}T00:00:00`).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

/**
 * An INSTANT (created_at), rendered in the reader's own zone.
 *
 * Right for "when was this typed", which is a moment — unlike a period boundary, which is a business
 * day and must read the same everywhere.
 */
const fmtInstant = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "—";

/**
 * Where credits go when nobody chose — the quarterly rotation, and every list that came before it.
 *
 * The screen's one job beyond editing is to keep two ideas apart: the list IN FORCE today, and the
 * newest list, which may be scheduled for a future quarter. They are usually the same row and
 * occasionally not, and a console that showed only "active" would say a January list was live in
 * December.
 */
function SetCard({
  set,
  onEdit,
  onDelete,
  deleting,
}: {
  set: DefaultCauseSet;
  onEdit: (set: DefaultCauseSet) => void;
  onDelete: (set: DefaultCauseSet) => void;
  deleting: boolean;
}) {
  const label = set.inForce ? "In force now" : set.scheduled ? "Scheduled" : "Past";
  const colour = set.inForce
    ? "var(--color-rodeo-black)"
    : set.scheduled
      ? "var(--color-dark-dust)"
      : "var(--color-graphite)";

  return (
    <Box
      sx={{
        border: `1px solid ${set.inForce ? "var(--color-dark-dust)" : "var(--color-card-border)"}`,
        borderRadius: "12px",
        p: 2,
        mb: 1.5,
        bgcolor: set.inForce ? "var(--color-clear-dust)" : "transparent",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Chip size="small" label={label} sx={{ bgcolor: colour, color: "#fff", fontWeight: 700 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {fmtDate(set.effectiveFrom)} — {set.effectiveTo ? fmtDate(set.effectiveTo) : "ongoing"}
        </Typography>
        {set.note && (
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            · {set.note}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {/* Driven by the server's `stage`, so the screen never offers what the API would refuse.
            An ended list shows neither: credits were minted against it and snapshotted their
            destinations from it, so changing it now would make the record disagree with what was
            actually funded. */}
        {set.stage !== "ended" && (
          <Button size="small" onClick={() => onEdit(set)} sx={{ minWidth: 0 }}>
            Edit causes
          </Button>
        )}
        {set.stage === "scheduled" && (
          <Button
            size="small"
            color="error"
            disabled={deleting}
            onClick={() => onDelete(set)}
            sx={{ minWidth: 0 }}
          >
            Delete
          </Button>
        )}
      </Box>

      {set.causes.map((c) => (
        <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.35, pl: 0.5 }}>
          <Typography sx={{ fontSize: 13.5, flex: 1, minWidth: 0 }}>
            <strong>{c.organization}</strong> — {c.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: "var(--color-graphite)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {c.price != null ? formatUsd(c.price) : "—"} · {Math.round(c.share * 100)}%
          </Typography>
        </Box>
      ))}

      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 1 }}>
        Set by {set.createdByName ?? "—"} on {fmtInstant(set.createdAt)}
      </Typography>
    </Box>
  );
}

export default function DefaultCausesPanel() {
  const { data: sets = [], isLoading, error } = useDefaultCauseSets();
  const remove = useDeleteDefaultCauseSet();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DefaultCauseSet | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // The head of the chain — what a new list must start after. Not necessarily the one in force.
  const head = sets.find((s) => s.isActive) ?? null;
  const inForce = sets.find((s) => s.inForce) ?? null;
  const nextUp = [...sets].reverse().find((s) => s.scheduled) ?? null;

  function openNew() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(set: DefaultCauseSet) {
    setEditing(set);
    setDialogOpen(true);
  }

  async function handleDelete(set: DefaultCauseSet) {
    setActionError(null);
    try {
      await remove.mutateAsync(set.id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Couldn't delete the cause list.");
    }
  }

  return (
    <Box sx={{ maxWidth: 780 }}>
      <Box
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}
      >
        <Box sx={{ pr: 2 }}>
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 20 }}>
            Default causes
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: "var(--color-graphite)", mt: 0.5 }}>
            Where unused credits go when the buyer didn't choose a cause — an invoice, a goodwill
            adjustment, an automated top-up. Credits keep the list that was in force on the day they
            were bought.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={openNew}
          sx={{ bgcolor: "var(--color-rodeo-black)", flexShrink: 0 }}
        >
          Set new cause list
        </Button>
      </Box>

      {isLoading && <Typography sx={{ fontSize: 13 }}>Loading…</Typography>}
      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* THE STATE THAT USED TO BE INVISIBLE. Lists can all be scheduled, in which case nothing is
          in force and the built-in list is quietly doing the work — which looks identical to
          "configured" unless the screen says otherwise. */}
      {!isLoading && !error && !inForce && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>No list is in force.</strong> New credits are using the built-in default until one
          starts
          {nextUp ? ` — the next begins ${fmtDate(nextUp.effectiveFrom)}.` : "."}{" "}
          {nextUp
            ? "To have a list apply today, delete the scheduled one and set a new one starting today."
            : "Set a list starting today to take over immediately."}
        </Alert>
      )}

      {!isLoading && sets.length === 0 && (
        <Alert severity="warning">
          No list has been set. Until one is, defaults fall back to the list held in code — set one
          here so the choice is yours rather than a deploy's.
        </Alert>
      )}

      {sets.map((s) => (
        <SetCard
          key={s.id}
          set={s}
          onEdit={openEdit}
          onDelete={handleDelete}
          deleting={remove.isPending}
        />
      ))}

      <SetCauseListDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        currentFrom={head?.effectiveFrom ?? null}
        editing={editing}
      />
    </Box>
  );
}
